import React, { useState, useEffect, useRef } from 'react';
import { MDBRow, MDBCol } from 'mdbreact';
import { post } from 'axios';
import PropTypes from 'prop-types';

import { pdiIcons } from 'pages/PDI/utils';

import { renderToast } from 'components/Informations/Toast/utils';
import PageStructure from 'components/PageStructure';

import Slider from 'components/Slider';
import ContentLoader from 'components/Informations/Loaders/ContentLoader';
import Tooltip from 'components/Informations/Tooltip';

import FIleButton from 'components/FormGroup/FIleButton';
import Label from 'components/FormGroup/Label';

import FilledButton from 'components/Buttons/FilledButton';

import endpoints from 'services/endpoints';
import useFetch from 'hooks/useFetch';
import {
  getKnowledgeLevel,
  toBase64,
  knowledgeLevelInsideArray,
} from './utils';

import Certified from '../../InternalComponents/InformationBlocks/Certified';
import KnowledgeLevels from '../../InternalComponents/InformationBlocks/KnowledgeLevels';

import * as S from './styles';

const {
  getEvaluation,
  uploadCertificates,
  uploadOthersCertificates,
} = endpoints;

export default function Resume({ history, location, user }) {
  const { state } = location;
  const { context } = state;
  const { id_funcionario: idFuncionario } = user;

  const [loading, setLoading] = useState(true);
  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastInfos, setToastInfos] = useState({
    type: '',
    message: '',
    closeTime: 6000,
  });

  const [certificatesQuantity, setCertificatesQuantity] = useState(1);
  const [uploadedCertificates, setUploadedCertificates] = useState([]);

  const [resumeAreas, setResumeAreas] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentFluxURL =
    context === 'team-evaluation'
      ? `${getEvaluation}/0/0/0/0/${state?.data?.idConfig}`
      : context === 'self-evaluation'
      ? `${getEvaluation}/0/0/0/${state?.data?.idFuncionario}/0`
      : context === 'evaluation'
      ? `${getEvaluation}/${state?.data?.idAvaliador}/${state?.data?.idAvaliacao}/0/0/0`
      : `${getEvaluation}/0/${state?.data?.idAvaliacao}/${state?.data?.idMediador}/0/0`;

  const [evaluationId, setEvaluationId] = useState('');
  const [allAnswers] = useFetch(currentFluxURL);

  const zipRef = useRef();

  useEffect(() => {
    (async () => {
      const response = await allAnswers;

      const {
        respostas,
        id_avaliacao: idAvaliacao,
        num_certificados: certificados,
      } = response;

      setCertificatesQuantity(certificados || 0);
      setResumeAreas(respostas || []);
      setEvaluationId(idAvaliacao);

      return respostas ? setLoading(false) : setLoading(true);
    })();
  }, [allAnswers]);

  const removeUploadedCertificate = id => {
    setCertificatesQuantity(currentQuantity => currentQuantity + 1);

    return setUploadedCertificates(current =>
      current.filter(({ idQuestao }) => idQuestao !== id),
    );
  };

  const handleCertificateUpload = (file, value, questionId) => {
    const duplicates = uploadedCertificates?.filter(
      ({ idQuestao }) => idQuestao === questionId,
    );

    if (!file.name) return;

    if (duplicates && duplicates.length >= 1) {
      removeUploadedCertificate(questionId);
    }

    setCertificatesQuantity(currentQuantity => currentQuantity - 1);

    setUploadedCertificates(current => [
      ...current,
      {
        idQuestao: questionId,
        value,
        file,
      },
    ]);
  };

  const renderResumeAnswers = data =>
    data.map(
      ({
        id_questao_template: idQuestao,
        nome,
        descricao,
        pontuacao,
        resposta_textual: respostaTextual,
        certificado,
        opcoes_resposta: opcoes,
        alternativas,
      }) => {
        const currentFileItem = fileIndex => {
          if (!uploadedCertificates.length) return '';

          const requiredFile = uploadedCertificates?.filter(
            element => element.idQuestao === fileIndex,
          );

          if (requiredFile && requiredFile.length > 0) {
            const { value } = requiredFile[0];
            return value;
          }

          return '';
        };

        return (
          <S.Question key={idQuestao}>
            {!!descricao && (
              <Tooltip position="right">
                <p>{descricao}</p>
              </Tooltip>
            )}

            <S.Description>{nome}</S.Description>
            <S.Knowledge className={respostaTextual ? 'type-text' : ''}>
              {respostaTextual || opcoes
                ? knowledgeLevelInsideArray(pontuacao, opcoes, alternativas)
                : getKnowledgeLevel(pontuacao)}
            </S.Knowledge>

            {certificado && (
              <S.HasCertified>
                {pdiIcons('certifiedKnowledge')}
                {pdiIcons('pdf')}
                <FIleButton
                  fileType="pdf"
                  maxMBSize={5}
                  currentValue={() => currentFileItem(idQuestao)}
                  inputName={`certificate-${idQuestao}`}
                  handleChange={(file, value) => {
                    handleCertificateUpload(file, value, idQuestao);
                  }}
                  handleReset={() => removeUploadedCertificate(idQuestao)}
                />
              </S.HasCertified>
            )}
          </S.Question>
        );
      },
    );

  const renderResumeBlocks = data => {
    if (!data) return [];

    const { blocos } = data;

    return blocos.map(({ nome, descricao, questoes }) => (
      <>
        <S.Head>
          {!!descricao && (
            <Tooltip position="right">
              <p>{descricao}</p>
            </Tooltip>
          )}
          <S.AreaName>{nome}</S.AreaName>
          <S.KnowledgeLevel>Nível de conhecimento</S.KnowledgeLevel>
        </S.Head>
        <S.Answers>{renderResumeAnswers(questoes)}</S.Answers>
      </>
    ));
  };

  const handleSubmit = async () => {
    setLoading(true);

    const formData = new FormData();
    const { files } = zipRef.current;
    const currentFile = files[0];

    formData.append('certificados', currentFile);
    formData.append('id_funcionario', state?.data?.idFuncionario);
    formData.append('id_usr_in', idFuncionario);
    formData.append('id_usr_up', idFuncionario);
    formData.append('id_avaliacao', evaluationId);

    const allCertificates =
      (await Promise.all(
        uploadedCertificates?.map(async ({ idQuestao, file }) => ({
          id_questao: idQuestao,
          body: await toBase64(file),
        })),
      )) || [];

    const submitObject = {
      id_funcionario: state?.data?.idFuncionario,
      id_avaliacao: evaluationId,
      certificados: allCertificates,
    };

    try {
      if (currentFile) {
        return await post(uploadOthersCertificates, formData)
          .then(() => {
            setToastVisibility(true);
            setToastInfos({
              type: 'success',
              message: 'Avaliação e .ZIP salvos com sucesso',
              closeTime: 6000,
            });
          })
          .then(() =>
            setTimeout(() => {
              history.push('/auth/pdi');
            }, 3000),
          );
      }

      return await post(uploadCertificates, submitObject)
        .then(() => setLoading(false))
        .then(() => {
          setToastVisibility(true);
          setToastInfos({
            type: 'success',
            message: 'Avaliação salva com sucesso',
            closeTime: 6000,
          });
        })
        .then(() =>
          setTimeout(() => {
            history.push('/auth/pdi');
          }, 3000),
        );
    } catch {
      setLoading(false);
      setToastVisibility(true);

      return setToastInfos({
        type: 'error',
        message: 'Erro ao submeter certificados. Tente novamente',
        closeTime: 6000,
      });
    }
  };

  return (
    <>
      {loading && <ContentLoader isFull />}

      {toastVisibility &&
        renderToast(
          toastInfos.type,
          toastInfos.message,
          toastInfos.closeTime,
          setToastVisibility,
        )}

      <PageStructure
        title="Plano de Desenvolvimento Individual - Resumo"
        subtitle="Revise suas respostas navegando por área e edite, se necessário.
        Faça upload dos certificados ao lado de cada ferramenta.
        Para certificados acadêmicos ou generalistas, suba o(s) arquivo(s) no campo lateral.
        Ao final, envie a avaliação."
      >
        <MDBRow>
          <MDBCol lg="9">
            <S.Content>
              <Slider
                data={resumeAreas}
                currentIndex={currentIndex}
                currentname={resumeAreas[currentIndex]?.descricao}
                setSelectedIndex={setCurrentIndex}
              >
                {renderResumeBlocks(resumeAreas[currentIndex])}
              </Slider>
            </S.Content>
            <S.ActionButtons>
              <FilledButton
                buttonClass="cancel"
                text="Editar respostas"
                handleClick={() => history.goBack()}
              />
              <FilledButton
                buttonClass="confirm"
                text="Enviar avaliação"
                disabled={loading || certificatesQuantity !== 0}
                hasTooltip
                handleClick={handleSubmit}
                tooltipPosition="bottom-left"
                tooltipText="Faça o upload de todos os certificados para enviar a avaliação."
              />
            </S.ActionButtons>
          </MDBCol>
          <MDBCol lg="3">
            <S.StickyContent>
              <S.OthersCertificates>
                <Label
                  text="Outros certificados"
                  htmlFor="othersCertificates"
                />
                <S.InputContainer>
                  {pdiIcons('zip')}
                  <FIleButton
                    inputName="othersCertificates"
                    fileType="zip"
                    labelText="Faça o upload"
                    maxMBSize={5}
                    ref={zipRef}
                  />
                </S.InputContainer>
                <S.Small>
                  Apenas .ZIP de até 5MB contendo somente arquivos .PDF
                </S.Small>
              </S.OthersCertificates>
              <KnowledgeLevels />
              <Certified />
            </S.StickyContent>
          </MDBCol>
        </MDBRow>
      </PageStructure>
    </>
  );
}

Resume.defaultProps = {
  history: {},
  location: {},
  user: {},
};

Resume.propTypes = {
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ),
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
